import * as TypeStyle from 'typestyle';
import { ListAndMenuColors, TextColors } from '../../styles/Colors';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { ListAndMenuItemHeight, ListAndMenuPadding, ListIsActiveClass } from '../List/ListStyles';

export const TreeNodeDisabledClassName = 'disabled';
export const TreeNodeLoadingClassName = 'loading';
export const TreeUnselectableParentsClassName = 'unselectable-parents';
export const TreeHeightCSSVarName = '--tree-height';
export const TreeNodeLevelCSSVarName = '--tree-node-level';
export const DefaultIconSize = 14;
export const CheckIconSize = 12; // check icon with surrounding border should appear as same size as other icons
const ChevronContainerSize = 24; // touch and pointer target size is bigger than the default icon size for better usability
const SpacingAroundIcons = (ChevronContainerSize - DefaultIconSize) / 2;
const MinWidthForContent = 100; // ensure some space for the label container since (24 + 14) pixels are reserved for the chevron and the check
const TreeIndentPixelsCSSVarName = '--tree-indent-pixels';
const TreeItemCheckboxMarginCSSVarName = '--treeitem-checkbox-margin';

export const RootContainerStyleName = TypeStyle.style({
  [TreeIndentPixelsCSSVarName as string]: ChevronContainerSize - SpacingAroundIcons,
  [TreeItemCheckboxMarginCSSVarName as string]: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const SearchFieldStyleName = TypeStyle.style({
  flexShrink: 0,
  marginLeft: 3,
  marginRight: 3,
  marginTop: 3,
  $nest: {
    '& input': {
      width: '100%',
    },
  },
});

export const SpinnerWrapperStyleName = TypeStyle.style({
  alignItems: 'center',
  display: 'inline-flex',
  height: '100%',
  justifyContent: 'center',
  overflowY: 'hidden', // hides the scrollbar when the spinner is rotating and changing the height slightly
  width: '100%',
});

export const CheckContainerStyleName = TypeStyle.style({
  border: `1px solid transparent`,
  borderRadius: 2,
  display: 'inline-flex',
  flexShrink: 0,
  margin: `var(${TreeItemCheckboxMarginCSSVarName})`,
  height: CheckIconSize,
  width: CheckIconSize,
});

const loadingTreeItem = `[role="treeitem"].${TreeNodeLoadingClassName}`;
const selectedTreeItem = '[role="treeitem"][aria-selected="true"]';
const enabled = `:not(.${TreeNodeDisabledClassName}, [aria-disabled="true"])`;
const selectedEnabledTreeItem = `${selectedTreeItem}${enabled}`;

export const TreeStyleName = TypeStyle.style({
  color: TextColors.Primary,
  flexGrow: 1,
  fontSize: 12,
  listStyle: 'none',
  margin: 0,
  overflow: 'auto',
  padding: ListAndMenuPadding,
  $nest: {
    [`&[aria-multiselectable="true"] .${CheckContainerStyleName}`]: {
      borderColor: 'currentcolor',
      order: -1,
    },
    [`&.${TreeUnselectableParentsClassName}[aria-multiselectable="true"] [role="treeitem"][aria-expanded] .${CheckContainerStyleName},
      &[aria-multiselectable="true"] ${loadingTreeItem} .${CheckContainerStyleName}`]: {
      borderColor: 'transparent',
    },
    [`&.${TreeUnselectableParentsClassName}[aria-multiselectable="true"] .${CheckContainerStyleName},
      &[aria-multiselectable="true"] ${loadingTreeItem} .${CheckContainerStyleName}`]: {
      marginLeft: -(DefaultIconSize + SpacingAroundIcons),
    },
    '& ul': {
      listStyle: 'none',
      margin: 0,
      paddingLeft: 0,
    },
    '&:focus-visible': DefaultFocusIndicator(),
    [`&:focus-visible ${selectedTreeItem}, :focus-within > & ${selectedTreeItem}, &.${ListIsActiveClass} ${selectedTreeItem}`]: {
      borderColor: ListAndMenuColors.ActiveBorder,
    },
    [`&:focus-visible ${selectedEnabledTreeItem}, :focus-within > & ${selectedEnabledTreeItem}, &.${ListIsActiveClass} ${selectedEnabledTreeItem}`]: {
      backgroundColor: ListAndMenuColors.Hover,
    },
  }
});

export const SectionTitleStyleName = TypeStyle.style({
  ...PreventUserSelect,
  alignItems: 'center',
  display: 'flex',
  fontWeight: 'bold',
  minHeight: ListAndMenuItemHeight,
  paddingLeft: `calc(var(${TreeNodeLevelCSSVarName}) * var(${TreeIndentPixelsCSSVarName}) + ${SpacingAroundIcons}px)`,
});

export const SeparatorStyleName = TypeStyle.style({
  background: '#C9C9C9', // elevation/border/on-light/default
  height: 1,
  marginBottom: 8,
  marginLeft: `calc(var(${TreeNodeLevelCSSVarName}) * var(${TreeIndentPixelsCSSVarName}))`,
  marginTop: 8,
  minWidth: `calc((var(${TreeHeightCSSVarName}) - var(${TreeNodeLevelCSSVarName})) * var(${TreeIndentPixelsCSSVarName}) + ${MinWidthForContent}px)`,
});

export const TreeItemStyleName = TypeStyle.style({
  ...PreventUserSelect,
  alignItems: 'center',
  border: '1px solid transparent',
  borderRadius: 2,
  boxSizing: 'border-box',
  display: 'flex',
  minHeight: ListAndMenuItemHeight,
  minWidth: `calc(var(${TreeHeightCSSVarName}) * var(${TreeIndentPixelsCSSVarName}) + ${MinWidthForContent}px)`,
  paddingLeft: `calc(var(${TreeNodeLevelCSSVarName}) * var(${TreeIndentPixelsCSSVarName}))`,
  $nest: {
    [`&:hover${enabled}`]: {
      backgroundColor: ListAndMenuColors.Hover,
      borderColor: ListAndMenuColors.ActiveBorder,
    },
    [`&&:active${enabled}`]: {
      backgroundColor: ListAndMenuColors.Press,
      borderColor: ListAndMenuColors.ActiveBorder,
    },
  }
});

export const ChevronContainerStyleName = TypeStyle.style({
  display: 'inline-flex',
  flexShrink: 0,
  height: ChevronContainerSize,
  width: ChevronContainerSize,
  $nest: {
    '& svg': {
      cursor: 'pointer',
      padding: SpacingAroundIcons,
    },
    '[aria-expanded="true"] > & svg': {
      transform: 'rotate(90deg)',
    },
  }
});

export const ContentContainerStyleName = TypeStyle.style({
  display: 'inline-flex',
  flexGrow: 1,
  gap: SpacingAroundIcons,
  paddingBottom: SpacingAroundIcons,
  paddingRight: SpacingAroundIcons,
  paddingTop: SpacingAroundIcons,
  $nest: {
    [`.${TreeNodeDisabledClassName} > &`]: {
      color: TextColors.Disabled,
    },
  }
});

export const LabelContainerStyleName = TypeStyle.style({
  display: 'inline-flex',
  flexGrow: 1,
  wordBreak: 'break-word', // in case the text is very long without any spaces
});
